import { PORTAL_VIEW_NAME } from "../core/constants";
import { getDomainCfg } from "../core/state";
import { getViewData, getViewState } from "../core/views";
import { checkUser } from "./cloudstorage/user";


// Verhindert doppelte Auth-Aufrufe (Race-Condition Prävention)
let authInProgress = false;
let authPromise: Promise<any> | null = null;

// Authentifizieren eines Benutzers (mit Race-Condition Prävention)
async function authenticateUserInternal(): Promise<any> {
  try {
    let authToken;
    if (getDomainCfg().chatSettings.noAuth !== true) {
      // Verwende die baseUrl des WordPress-Servers für den Auth-Endpunkt
      const wpAuthUrl = `${getDomainCfg().serverSettings.baseUrl}/wp-json/api-user-guard/v1/auth`;
      
      const options: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
      };

      try {
        const response = await fetch(wpAuthUrl, options);
        if (!response.ok) {
          return { error: true, message: `Server responded with an error: ${response.status}` };
        }
        const { token } = await response.json();
        authToken = token;
      } catch (error) {
        return { error: true, message: error.message || 'An error occurred during authentication' };
      }
    } else {
      const authUrl = `${getDomainCfg().chatSettings.chatUrl}auth`;
      const options: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: {          
          'Cache-Control': 'no-cache'
        },
      };
      try {
        const response = await fetch(authUrl, options);
        console.log('auth: response', response);
        const { token } = await response.json();
        authToken = token;
      } catch (error) {
        console.error('Error fetching auth token:', error);
        return { error: true, message: error.message };
      }
    }

    // Schritt 2: Initialisiere die Session am Chat-Server mit dem erhaltenen Token
    const chatSessionUrl = `${getDomainCfg().chatSettings.chatUrl}session`;
    const options2: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Cache-Control': 'no-cache'
      },
    };
    if (authToken) {
      options2.headers['Authorization'] = `Bearer ${authToken}`;
    }

    const sessionResponse = await fetch(chatSessionUrl, options2);
    if (!sessionResponse.ok) {
      return { error: true, message: `Chat-Server responded with an error: ${sessionResponse.status}` };
    }

    console.log('auth: user-session', sessionResponse);
    // Schritt 3: Speichere das Token für zukünftige Requests
    localStorage.setItem('sessionToken', authToken);
    return sessionResponse.json();
  } catch (error) {
    console.error('Authentifizierungsfehler:', error);
    return { error: true, message: error.message };
  }
}

// Öffentliche authenticateUser Funktion mit Race-Condition Prävention
export async function authenticateUser(): Promise<any> {
  // Prüfe ob Token bereits vorhanden ist
  const existingToken = localStorage.getItem('sessionToken');
  if (existingToken) {
    // Token existiert, prüfe ob Session noch gültig ist durch /session Request
    try {
      const chatSessionUrl = `${getDomainCfg().chatSettings.chatUrl}session`;
      const options: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache',
          'Authorization': `Bearer ${existingToken}`
        },
      };
      const sessionResponse = await fetch(chatSessionUrl, options);
      const sessionData = await sessionResponse.json();
      if (sessionResponse.ok && !sessionData.error && sessionData.status !== 'error') {
        return sessionData;
      }
      // Session ungültig (Token abgelaufen oder Fehler), mache neue Auth
      // Lösche abgelaufenes Token
      console.log('Session check failed, making new auth:', sessionData);
      localStorage.removeItem('sessionToken');
    } catch (error) {
      // Session ungültig, mache neue Auth
      localStorage.removeItem('sessionToken');
    }
  }

  // Wenn Auth bereits läuft, warte auf das bestehende Promise
  if (authInProgress && authPromise) {
    return await authPromise;
  }

  // Starte Auth-Prozess
  authInProgress = true;
  authPromise = authenticateUserInternal();
  
  try {
    const result = await authPromise;
    authInProgress = false;
    authPromise = null;
    return result;
  } catch (error) {
    authInProgress = false;
    authPromise = null;
    throw error;
  }
}

/**
 * Holt die aktuelle Session inkl. Quota vom Chat-Server (für Debug: Quota vorher/nachher).
 * @returns Session-Objekt mit quota.credits oder null bei Fehler
 */
export async function getSession(): Promise<{ quota?: { credits?: number }; status?: string } | null> {
  const token = localStorage.getItem('sessionToken');
  if (!token) return null;
  const chatSessionUrl = `${getDomainCfg().chatSettings.chatUrl}session`;
  try {
    const response = await fetch(chatSessionUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

async function ensureAuthenticated(): Promise<boolean> {
  try {
    const result = await authenticateUser();
    return !result.error;
  } catch (error) {
    return false;
  }
}

// Gemeinsame Funktion für Task-Requests mit garantierter Authentifizierung
export async function executeTaskRequest(taskData: any): Promise<any> {
  // Stelle sicher, dass Auth/Session vorher durchläuft
  const isAuthenticated = await ensureAuthenticated();
  if (!isAuthenticated) {
    throw new Error('Authentifizierung fehlgeschlagen');
  }

  const authToken = localStorage.getItem('sessionToken');
  const options: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify(taskData),
  };
  
  if (authToken) {
    options.headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(getDomainCfg().chatSettings.chatUrl + 'task', options);
  if (!response.ok) {
    throw new Error(`Server responded with an error: ${response.status}`);
  }
  return await response.json();
}

// Setzen des Benutzernamens
export async function setNickname(nickname: string): Promise<any> {
  try {
    const authToken = localStorage.getItem('sessionToken');
    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ nickname }),
    };

    const response = await fetch(getDomainCfg().chatSettings.chatUrl + 'set-nickname', options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error setting nickname:', error);
    throw error;
  }
}

// Initialisieren eines Workflows
export async function initializeWorkflow(workflowName: string, wfDefinition? : any, messages? : any[], model?: string): Promise<any> {
  try {
    const authToken = localStorage.getItem('sessionToken');
    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ workflowName, definition : wfDefinition, history : messages, model }),
    };

    const response = await fetch(getDomainCfg().chatSettings.chatUrl + 'initialize-workflow', options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error initializing workflow:', error);
    throw error;
  }
}

// Senden einer Nachricht
export async function sendMessage(roomId: string, message: string): Promise<any> {
  try {
    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ roomId, message }),
    };

    const response = await fetch(getDomainCfg().chatSettings.chatUrl + 'message', options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}

// Stoppen eines Workflows
export async function stopWorkflow(sessionId: string): Promise<any> {
  try {
    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ sessionId }),
    };

    const response = await fetch(getDomainCfg().chatSettings.chatUrl + 'stop-workflow', options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error stopping workflow:', error);
    throw error;
  }
}

// Abrufen des Workflow-Status
export async function fetchWorkflowStatus(sessionId: string): Promise<any> {
  try {
    const options: RequestInit = {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }      
    };
    const response = await fetch(getDomainCfg().chatSettings.chatUrl + `workflow-status/${sessionId}`, options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching workflow status:', error);
    throw error;
  }
}

export async function fetchRoomDetails(roomId) {
  try {
    const response = await fetch(getDomainCfg().chatSettings.chatUrl + `room-details/${roomId}`, {
      method: 'GET',
      credentials: 'include', // Ermöglicht das Senden von Credentials wie Cookies
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching room details:', error);
    throw error;
  }
}

export async function fetchRoomMessages(roomId) {
  try {
    const response = await fetch(getDomainCfg().chatSettings.chatUrl + `room-messages/${roomId}`, {
      method: 'GET',
      credentials: 'include', // Ermöglicht das Senden von Credentials wie Cookies
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching room messages:', error);
    throw error;
  }
}

// Evaluieren des Chats mithilfe des LLM-Tasks
export async function executeLLMEvalTask(subtask: string, messages: any[]): Promise<any> {
  try {
    return await executeTaskRequest({
      name: 'eval',
      messages: JSON.stringify(messages),
      subtask: subtask
    });
  } catch (error) {
    console.error('Error executing LLM eval task:', error);
    throw error;
  }
}

// Starten eines Projektes über einen LLM-Task
export async function executeProjectStarterTask(llmTask: string, query: string): Promise<any> {
  try {
    await checkUser(false);
    return await executeTaskRequest({
      name: llmTask,
      query: query,
      projects: createProjectListForPrompt()
    });
  } catch (error) {
    console.error('Error executing project starter task:', error);
    throw error;
  }
}

export function prepareChatMessagesForProject(llmAnswer, query) {
  const messages = [];
  let firstBotMessage = 'Hi!\n';
  if (llmAnswer.einschätzung) {    
    firstBotMessage += llmAnswer.einschätzung;
  }
  if (llmAnswer.fragen) {
    firstBotMessage += '\n\n*Bevor wir starten:*\n';
    firstBotMessage += '\n' + llmAnswer.fragen.map((frage, index) => ` ${index+1}. ${frage}`).join('\n');
  }
  messages.push({ sender: getDomainCfg().chatSettings.userName, message: query, timestamp: new Date().toISOString() });
  messages.push({ sender: getDomainCfg().chatSettings.botName, message: firstBotMessage, timestamp: new Date().toISOString() });
  return messages;

}

export async function executeProjectNameTask(messages: any[]): Promise<any> {
  try {
    return await executeTaskRequest({
      name: 'project',
      query: JSON.stringify(messages)
    });
  } catch (error) {
    console.error('Error executing ProjectName task:', error);
    throw error;
  }
}

export async function fetchUserCredits(): Promise<any> {
  try {
    const authToken = localStorage.getItem('sessionToken');
    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        //'Authorization': `Bearer ${authToken}`
      },
    };

    const wpUrl = `${getDomainCfg().serverSettings.baseUrl}/wp-json/api-user-guard/v1/check-quota`;
    const response = await fetch(wpUrl, options);
    if (!response.ok) {
      throw new Error(`Server responded with an error: ${response.status}`);
    }
    const creditData = await response.json();
    return creditData;
  } catch (error) {
    console.error('Error fetching user credits:', error);
    throw error;
  }
}

export function createProjectListForPrompt() {
  const portalState = getViewData(PORTAL_VIEW_NAME);

  return portalState.cfg?.templates.map((project: any) => {
    return `- ${project.code}: ${project.title} - ${project.description}`;
  }).join('\n');
}
